import React, { useMemo, useState, useContext } from 'react';
import clsx from 'clsx';
import { toast } from 'react-toastify';
import { ChevronArrowDown } from '@td-client-frontend/icons';
import { ButtonWithIconAndLoader } from '@td-client-frontend/common';
import { Button } from '../../Button';
import { AffectedGroups, ProfileName, OrgOwnership } from './';
import {
  ProfileType,
  useAssignProfile,
  useSaveAsProfile,
  useMarketingProfileCache
} from '../../../hooks/useMarketingProfile';
import { OrgByIdProps } from './OrgOwnership';
import Modal from '../../Modal/NewModal';
import { useAuth } from '../../../context/AuthContext';
import { PermMode } from '../../WithPermission';
import { PendingChangesContext } from '../../../context/PendingChangesContext';
import { alwaysSet } from '../../../utils/marketingUtils';
import { useMarketingSearchCache } from '../../../hooks/useMarketingPreferences';
import { Permissions } from '../../../@types/Permissions';
import { MarketingHierarchyRowData, MarketingProfileById } from '../../../@types/Marketing';

interface SaveAsProps {
  closeModal: () => void;
  groupOrgData: MarketingHierarchyRowData;
  profileData: MarketingProfileById;
}

const stepTitles: string[] = ['Affected entities', 'Profile name', 'Org ownership', 'Confirmation'];

export default function SaveAs({ closeModal, groupOrgData, profileData }: SaveAsProps) {
  const { checkPerm } = useAuth();
  const { setIsChanged } = useContext(PendingChangesContext);
  const { saveAsProfile, isLoading: saveProfileLoading } = useSaveAsProfile();
  const { assignProfile, isLoading: assignProfileLoading } = useAssignProfile(groupOrgData?.id);
  const { invalidateMarketingSearchCache } = useMarketingSearchCache();
  const { setProfileCache } = useMarketingProfileCache();

  const [step, setStep] = useState<number>(0);
  const [nextButtonDisabled, setNextButtonDisabled] = useState<boolean>(true);
  const [newProfileName, setNewProfileName] = useState<string>('');
  const [ownership, setOwnership] = useState<OrgByIdProps | null>(null);

  const isLoading = useMemo(
    () => saveProfileLoading || assignProfileLoading,
    [assignProfileLoading, saveProfileLoading]
  );

  const handleAffectedGroupsOnChange = (v: number | null) => setNextButtonDisabled(v === null);

  const handleSave = async () => {
    const hasBaseCreate: boolean = checkPerm([Permissions.MarketingProfileBaseCreate], PermMode.ALL);
    if (ownership) {
      setIsChanged(false);
      const prevProfileId = profileData.id;

      const res = await saveAsProfile({
        name: newProfileName,
        ownership,
        settings: profileData.settings,
        baseFlg: hasBaseCreate ? profileData.default : false
      });
      if (res?.id) {
        const { error } = await assignProfile(res.id);
        if (!error) {
          toast.success('Profile successfully created', {
            position: 'top-center',
            hideProgressBar: true,
            pauseOnHover: true,
            draggable: true,
            autoClose: 10000
          });
        }
      }

      invalidateMarketingSearchCache();
      setProfileCache(prevProfileId, null);
      closeModal();
    }
  };

  const handleNextClick = () => {
    setStep(step + 1);
    const isNextBtnDisabled = step === 1 && !ownership;
    setNextButtonDisabled(isNextBtnDisabled);
  };

  const handleProfileNameOnValid = (n: string) => {
    setNewProfileName(n);
    setNextButtonDisabled(false);
  };

  const handleOrgOwnerOnChange = (item: OrgByIdProps) => {
    setOwnership(item);
    setNextButtonDisabled(false);
  };

  const handleClickBack = () => {
    setStep((prev) => prev - 1);
  };

  const stepData = useMemo(() => {
    switch (step) {
      case 0:
        return {
          header: 'Affected group(s)',
          content: (
            <AffectedGroups
              id={groupOrgData?.id!}
              type={ProfileType.ORGANIZATION}
              onChange={handleAffectedGroupsOnChange}
              profileName={profileData.name}
            />
          ),
          disabled: nextButtonDisabled,
          progress: 'w-1'
        };
      case 1:
        return {
          header: 'Enter a name for your new profile.',
          content: (
            <ProfileName
              onValidName={handleProfileNameOnValid}
              onInvalidName={alwaysSet(setNewProfileName, '')}
              onChange={alwaysSet(setNextButtonDisabled, true)}
            />
          ),
          disabled: nextButtonDisabled,
          progress: 'w-1/4'
        };
      case 2:
        return {
          header: 'Select organization ownership.',
          content: <OrgOwnership id={groupOrgData!.id!} owner={ownership} onChange={handleOrgOwnerOnChange} />,
          disabled: nextButtonDisabled,
          progress: 'w-1/2'
        };
      case 3:
        return {
          header: 'Settings confirmation',
          content: (
            <>
              <p className="flex mb-6 text-lg font-medium">
                <span className="text-gray-55 mr-2">Profile name:</span>
                <span className="text-gray-45">{newProfileName}</span>
              </p>
              <p className="flex mb-7 text-lg font-medium">
                <span className="text-gray-55 mr-2">Org. ownership:</span>
                <span className="text-gray-45">{ownership!.name}</span>
              </p>
              <AffectedGroups
                id={groupOrgData?.id!}
                type={ProfileType.ORGANIZATION}
                hideDescription
                profileName={profileData.name}
              />
            </>
          ),
          progress: 'w-3/4'
        };
    }
  }, [step, groupOrgData, profileData.name, nextButtonDisabled, newProfileName, ownership]);

  return (
    <div className="w-full">
      <div className="px-8 py-6">
        <div className="flex justify-between ">
          {stepTitles.map((str: string, i: number) => (
            <p key={str} className={clsx('text-lg', i === step ? 'font-semibold text-gray-60' : 'text-gray-60')}>
              {str}
            </p>
          ))}
        </div>
        <div className="mt-2 w-full h-2 bg-gray-10 rounded-l-md rounded-r-md">
          <div
            data-testid="progressBar"
            className={clsx(
              'h-full bg-blue-500 rounded-l-md rounded-r-md ease-in-out duration-300',
              stepData?.progress
            )}
          />
        </div>
      </div>
      <div className="px-8 h-72">
        <h2 className="mb-6 text-26 text-gray-45">{stepData?.header}</h2>
        {stepData?.content}
      </div>
      <div className="px-8 py-6 bg-gray-10 border-t-1 rounded-b-lg flex justify-between gap-2">
        <div>
          {step !== 0 && (
            <Button onClick={handleClickBack} kind="secondary" className="px-6 text-lg">
              <ChevronArrowDown color="#5B2F91" className="transform rotate-90 mr-2" />
              Back
            </Button>
          )}
        </div>
        <div className="flex justify-between gap-2">
          <Button onClick={closeModal} kind="secondary" className="px-6 text-lg" disabled={isLoading}>
            Cancel
          </Button>
          {step === 3 ? (
            <div className="w-28">
              <ButtonWithIconAndLoader
                title="Save"
                loading={isLoading}
                className="flex items-center justify-center font-effra btn h-full"
                labelClasses={clsx('text-normal', isLoading && 'hidden')}
                icon={null}
                onClick={handleSave}
              />
            </div>
          ) : (
            <Button onClick={handleNextClick} disabled={stepData?.disabled || false} className="px-6 text-lg">
              Next
              <ChevronArrowDown color="white" className="transform -rotate-90 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export const SaveAsModal = ({ closeModal, isOpen, groupOrgData, profileData }: SaveAsProps & { isOpen: boolean }) => {
  return (
    <Modal isOpen={isOpen} onClose={closeModal} header="Save as new profile" size="medium">
      <SaveAs closeModal={closeModal} groupOrgData={groupOrgData} profileData={profileData} />
    </Modal>
  );
};
